import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Autocomplete,
} from "@mui/material";
import styled from "styled-components";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import { updateInventoryItem } from "../../store/inventory/actions";
import {
  getAllInventory,
  updateInventoryImage,
  LowStock,
  AlertChannel,
} from "../../services/inventoryApi";
import { Backdrop, Field } from "../Auth/styled-component";
import DotLoader from "../../components/Loader/DotLoader";
import { MotionBox } from "./AddInventoryModal";
import { TextField } from "@mui/material";
import LowStockControlsWeb, {
  LowStockValue,
} from "../../components/LowStockControlsWeb";
import {
  createExpenseCategory,
  listExpenseCategories,
  type ExpenseCategory,
} from "../../services/financeApi";
const ModalWrapper = styled(Box)`
  max-height: 90vh;
  overflow-y: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 900px;
  width: 95%;
  margin: 5vh auto;
  padding: 2rem 2rem 3.5rem;
  display: flex;
  flex-direction: row;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch; /* 👈 ensures image container stretches */
  justify-content: center;

  @media (max-width: 768px) {
    min-height: 280px;
  }
`;

const RightPanel = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const VariantRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const AddVariantButton = styled(Button)`
  margin-top: 1rem;
  background: #e0e7ff;
  color: #1e40af;
`;

const RemoveButton = styled(IconButton)`
  && {
    background-color: transparent !important;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:hover {
      color: #dc2626;
    }

    .MuiTouchRipple-root {
      display: none;
    }
  }
`;

const ImageContainer = styled.label<{ isDragging?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 260px;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: ${({ isDragging }) =>
    isDragging ? "0 0 0 4px #2563eb" : "0 0 10px rgba(0, 0, 0, 0.1)"};
  background-color: ${({ isDragging }) =>
    isDragging ? "#f0f4ff" : "transparent"};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  transition:
    box-shadow 0.3s ease,
    background-color 0.3s ease;

  &:hover {
    box-shadow: 0 0 0 4px #2563eb; /* 🔵 blue hover */
    background-color: #f0f4ff;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: opacity 0.3s ease;
  }

  span {
    position: absolute;
    bottom: 0;
    width: 100%;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    font-size: 12px;
    padding: 0.4rem 0;
    text-align: center;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover span {
    opacity: 1;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

interface InventoryEditModalProps {
  open: boolean;
  onClose: () => void;
  item: any;
  onSave: (updatedItem: any) => void;
}

const InventoryEditModal: React.FC<InventoryEditModalProps> = ({
  open,
  onClose,
  item,
  onSave,
}) => {
  const getCategoryId = useCallback((source: any) => {
    const raw = source?.categoryId;
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object" && raw._id) return String(raw._id);
    return "";
  }, []);
  const getCategoryName = useCallback((source: any) => {
    if (!source) return "";
    const cat = source.categoryId;
    if (cat && typeof cat === "object" && typeof cat.name === "string") {
      return cat.name;
    }
    const raw = source.categoryName ?? source.category;
    return typeof raw === "string" ? raw.trim() : "";
  }, []);
  const makeSkuPrefix = useCallback((value: string) => {
    const normalized = value
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "")
      .trim();
    return normalized.slice(0, 3);
  }, []);
  const nextSkuForCategory = useCallback(
    (categoryName: string, items: Array<{ sku?: string }>) => {
      const prefix = makeSkuPrefix(categoryName);
      if (!prefix) return "";
      const highest = items.reduce((max, item) => {
        const rawSku = String(item?.sku || "")
          .trim()
          .toUpperCase();
        const matched = rawSku.match(new RegExp(`^${prefix}-(\\d{3})$`));
        if (!matched?.[1]) return max;
        return Math.max(max, Number(matched[1]));
      }, 0);
      return `${prefix}-${String(highest + 1).padStart(3, "0")}`;
    },
    [makeSkuPrefix],
  );

  const toFormData = useCallback(
    (source: any) => ({
      ...(source || {}),
      categoryId: getCategoryId(source),
    }),
    [getCategoryId],
  );

  const [formData, setFormData] = useState(toFormData(item));
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [inventoryItems, setInventoryItems] = useState<
    Array<{ _id: string; sku?: string; brand?: string }>
  >([]);
  const [categoryInput, setCategoryInput] = useState(getCategoryName(item));
  const [restockAmount, setRestockAmount] = useState("");
  const [finalQuantityInput, setFinalQuantityInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [skuAutoFillLocked, setSkuAutoFillLocked] = useState(false);
  const [autoGeneratedSku, setAutoGeneratedSku] = useState("");
  const [lowStock, setLowStock] = useState<LowStockValue>({
    enabled: !!item?.lowStock?.enabled,
    threshold: String(item?.lowStock?.threshold ?? ""),
    alertOnce: item?.lowStock?.alertOnce !== false, // default true
    cooldownHours: String(item?.lowStock?.cooldownHours ?? "24"),
    channels:
      Array.isArray(item?.lowStock?.channels) && item.lowStock.channels.length
        ? (item.lowStock.channels as AlertChannel[])
        : (["push"] as AlertChannel[]), // default
  });

  useEffect(() => {
    if (!item) return;
    setLowStock({
      enabled: !!item.lowStock?.enabled,
      threshold: String(item.lowStock?.threshold ?? ""),
      alertOnce: item.lowStock?.alertOnce !== false,
      cooldownHours: String(item.lowStock?.cooldownHours ?? "24"),
      channels:
        Array.isArray(item.lowStock?.channels) && item.lowStock.channels.length
          ? (item.lowStock.channels as AlertChannel[])
          : (["push"] as AlertChannel[]),
    });
  }, [item]);

  useEffect(() => {
    if (!open) return;
    listExpenseCategories("inventory")
      .then((rows) => setCategories(rows))
      .catch((error) =>
        console.error("Failed to load inventory categories:", error),
      );
    getAllInventory()
      .then((response) => setInventoryItems(response.data.items || []))
      .catch((error) =>
        console.error(
          "Failed to load inventory items for SKU generation:",
          error,
        ),
      );
  }, [open]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (item) setFormData(toFormData(item));
  }, [item, toFormData]);

  useEffect(() => {
    if (item) setCategoryInput(getCategoryName(item));
  }, [item, getCategoryName]);

  useEffect(() => {
    if (!open) return;
    setSkuAutoFillLocked(false);
    setAutoGeneratedSku("");
  }, [open, item?._id]);

  useEffect(() => {
    if (open) setRestockAmount("");
  }, [open, item]);

  useEffect(() => {
    if (!open) return;
    setFinalQuantityInput(String(toNum(formData.quantity) ?? 0));
  }, [open, item, formData.quantity]);

  useEffect(() => {
    if (!open || !formData.categoryId || categoryInput) return;
    const matched = categories.find((c) => c._id === formData.categoryId);
    if (matched) setCategoryInput(matched.name);
  }, [open, formData.categoryId, categoryInput, categories]);

  useEffect(() => {
    const categoryName = categoryInput.trim();
    const currentSku = String(formData.sku || "").trim();
    if (!categoryName || skuAutoFillLocked) return;
    const nextSku = nextSkuForCategory(categoryName, inventoryItems);
    const canReplaceCurrentSku =
      currentSku === "" || currentSku === autoGeneratedSku;
    if (!nextSku || !canReplaceCurrentSku || nextSku === currentSku) return;
    setFormData((prev: any) => ({ ...prev, sku: nextSku }));
    setAutoGeneratedSku(nextSku);
  }, [
    categoryInput,
    formData.sku,
    inventoryItems,
    nextSkuForCategory,
    skuAutoFillLocked,
    autoGeneratedSku,
  ]);

  const brandOptions = Array.from(
    new Set(
      inventoryItems
        .map((inventoryItem) => String(inventoryItem.brand || "").trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const handleChange = (key: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleAddVariant = () => {
    setFormData((prev: any) => ({
      ...prev,
      variants: [...(prev.variants || []), { attribute: "", value: "" }],
    }));
  };
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const previewURL = URL.createObjectURL(file);
      setFormData((prev: any) => ({ ...prev, imageUrl: previewURL }));
    }
  };

  const handleVariantChange = (
    index: number,
    key: "attribute" | "value",
    value: string,
  ) => {
    const updatedVariants = [...(formData.variants || [])];
    updatedVariants[index][key] = value;
    setFormData((prev: any) => ({ ...prev, variants: updatedVariants }));
  };

  const handleRemoveVariant = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      variants: (prev.variants || []).filter(
        (_: any, i: number) => i !== index,
      ),
    }));
  };
  const toNum = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    const s = String(v).trim();
    if (s === "") return undefined; // ← key: blank stays undefined
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleFinalQuantityChange = (rawValue: string) => {
    if (rawValue.trim() === "") {
      setFinalQuantityInput("");
      setRestockAmount("");
      return;
    }
    const finalQty = toNum(rawValue);
    setFinalQuantityInput(rawValue);
    if (finalQty === undefined) return;
    const currentQty = toNum(formData.quantity) ?? 0;
    setRestockAmount(String(finalQty - currentQty));
  };

  const handleQuantityChange = (rawValue: string) => {
    handleChange("quantity", rawValue);
    if (finalQuantityInput.trim() === "") return;
    const qty = toNum(rawValue) ?? 0;
    const addQty = toNum(restockAmount) ?? 0;
    setFinalQuantityInput(String(qty + addQty));
  };

  const handleRestockChange = (rawValue: string) => {
    setRestockAmount(rawValue);
    if (finalQuantityInput.trim() === "") return;
    const qty = toNum(formData.quantity) ?? 0;
    const addQty = toNum(rawValue) ?? 0;
    setFinalQuantityInput(String(qty + addQty));
  };

  const ensurePush = (chs?: AlertChannel[]) => {
    const arr = Array.isArray(chs) ? chs : [];
    return arr.includes("push") ? arr : (["push", ...arr] as AlertChannel[]);
  };

  const handleSave = async () => {
    if (!formData._id) return;
    setSaving(true);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("Missing auth token");
      const typedCategory = categoryInput.trim();
      let resolvedCategoryId = formData.categoryId;
      const finalQty = toNum(finalQuantityInput);

      if (
        finalQuantityInput.trim() === "" ||
        finalQty === undefined ||
        finalQty < 0
      ) {
        throw new Error("Final Quantity must be a number 0 or greater.");
      }

      const quantityToSave = finalQty;
      if (!resolvedCategoryId) {
        if (!typedCategory) throw new Error("Category is required.");
        const existing = categories.find(
          (c) => c.name.trim().toLowerCase() === typedCategory.toLowerCase(),
        );
        if (existing) {
          resolvedCategoryId = existing._id;
        } else {
          const created = await createExpenseCategory(
            typedCategory,
            "inventory",
          );
          resolvedCategoryId = created._id;
          setCategories((prev) =>
            [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
      }

      const lowStockPayload: LowStock = {
        enabled: !!lowStock.enabled,
        threshold: toNum(lowStock.threshold),
        alertOnce: lowStock.alertOnce !== false,
        cooldownHours: toNum(lowStock.cooldownHours) ?? 24,
        channels: lowStock.enabled
          ? ensurePush(lowStock.channels)
          : lowStock.channels,
      };

      const payload = {
        itemName: formData.itemName,
        quantity: quantityToSave,
        costPerUnit: toNum(formData.costPerUnit),
        defaultSellingPrice: toNum(formData.defaultSellingPrice),
        businessName: formData.businessName,
        brand: formData.brand,
        status: formData.status,
        sku: formData.sku,
        variants: formData.variants,
        notes: formData.notes,
        categoryId: resolvedCategoryId,
        lowStockEnabled: !!lowStockPayload.enabled,
        lowStockThreshold: lowStockPayload.threshold,
        lowStockAlertOnce: lowStockPayload.alertOnce,
        lowStockChannels: lowStockPayload.channels,
        lowStockCooldownHours: lowStockPayload.cooldownHours,
      };

      const updated = await dispatch(
        updateInventoryItem(formData._id, payload) as any,
      );
      let finalItem = updated || payload;

      if (selectedImage) {
        const imageResult = await updateInventoryImage(
          formData._id,
          selectedImage,
          token,
        );
        finalItem = imageResult.inventory ?? finalItem;
      }

      onSave(finalItem);

      onClose();
    } catch (err: any) {
      console.error("Inventory update failed:", err?.message || err);
      if (typeof window !== "undefined") {
        window.alert(
          err?.message
            ? `Failed to update inventory item: ${err.message}`
            : "Failed to update inventory item. Please try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled =
    saving ||
    finalQuantityInput.trim() === "" ||
    toNum(finalQuantityInput) === undefined ||
    (toNum(finalQuantityInput) ?? 0) < 0;

  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [open]);

  return (
    <Backdrop
      onClick={onClose}
      style={{
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? "visible" : "hidden",
      }}
    >
      <MotionBox
        position="relative"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: isVisible ? "translateY(0)" : "translateY(-50%)",
          opacity: isVisible ? 1 : 0,
        }}
      >
        <ModalWrapper>
          <LeftPanel>
            <ImageContainer
              htmlFor="image-upload"
              isDragging={isDragging}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith("image/")) {
                  const previewURL = URL.createObjectURL(file);
                  setSelectedImage(file);
                  setFormData((prev: any) => ({
                    ...prev,
                    imageUrl: previewURL,
                  }));
                }
              }}
            >
              <img
                src={
                  formData.imageUrl ||
                  "https://via.placeholder.com/260x360?text=No+Image"
                }
                alt="Preview"
              />
              <span>Click or drag to change image</span>
            </ImageContainer>

            <HiddenInput
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Edit Inventory
            </Typography>

            <FieldRow>
              <Field
                label="Item Name"
                value={formData.itemName || ""}
                onChange={(e) => handleChange("itemName", e.target.value)}
              />
              <Autocomplete
                fullWidth
                freeSolo
                options={brandOptions}
                value={formData.brand || ""}
                inputValue={formData.brand || ""}
                onInputChange={(_, newInput) => handleChange("brand", newInput)}
                onChange={(_, newValue) =>
                  handleChange(
                    "brand",
                    typeof newValue === "string" ? newValue : "",
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand Name"
                    placeholder="Pick saved brand or type a new one"
                  />
                )}
              />
            </FieldRow>

            <FieldRow>
              <Field
                label="Quantity"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={formData.quantity ?? ""}
                onChange={(e) => handleQuantityChange(e.target.value)}
              />
            </FieldRow>
            <FieldRow>
              <Field
                label="Add New Stock"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={restockAmount}
                onChange={(e) => handleRestockChange(e.target.value)}
              />
              <Field
                label="Final Quantity"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={finalQuantityInput}
                onChange={(e) => handleFinalQuantityChange(e.target.value)}
              />
            </FieldRow>

            <FieldRow>
              <Field
                label="Cost per Unit"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={formData.costPerUnit ?? ""}
                onChange={(e) => handleChange("costPerUnit", e.target.value)}
              />
              <Field
                label="Selling Price"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={formData.defaultSellingPrice ?? ""}
                onChange={(e) =>
                  handleChange("defaultSellingPrice", e.target.value)
                }
              />
            </FieldRow>

            <FieldRow>
              <Box flex={2}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={categories}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name
                  }
                  value={
                    categories.find((cat) => cat._id === formData.categoryId) ||
                    null
                  }
                  inputValue={categoryInput}
                  onInputChange={(_, newInput, reason) => {
                    setCategoryInput(newInput);
                    setSkuAutoFillLocked(false);
                    if (reason === "input") handleChange("categoryId", "");
                  }}
                  onChange={(_, newValue) => {
                    if (newValue && typeof newValue === "object") {
                      setCategoryInput(newValue.name);
                      setSkuAutoFillLocked(false);
                      handleChange("categoryId", newValue._id);
                      return;
                    }
                    if (typeof newValue === "string") {
                      setCategoryInput(newValue);
                      setSkuAutoFillLocked(false);
                    }
                    handleChange("categoryId", "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      placeholder="Type to filter or create"
                    />
                  )}
                />
              </Box>
              <Box flex={1}>
                <Field
                  label="SKU"
                  value={formData.sku || ""}
                  onChange={(e) => {
                    setSkuAutoFillLocked(true);
                    setAutoGeneratedSku("");
                    handleChange("sku", e.target.value);
                  }}
                />
              </Box>
            </FieldRow>

            <FieldRow>
              <Field
                label="Business Name"
                value={formData.businessName || ""}
                onChange={(e) => handleChange("businessName", e.target.value)}
                sx={{ mb: 2 }}
              />
            </FieldRow>
            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              multiline
              minRows={4}
              fullWidth
              sx={{ mb: 1 }}
            />
            <Typography variant="subtitle1" mb={1}>
              Variants
            </Typography>

            {(formData.variants || []).map((variant: any, index: number) => (
              <VariantRow key={index}>
                <Field
                  label="Attribute"
                  value={variant.attribute}
                  onChange={(e) =>
                    handleVariantChange(index, "attribute", e.target.value)
                  }
                />
                <Field
                  label="Value"
                  value={variant.value}
                  onChange={(e) =>
                    handleVariantChange(index, "value", e.target.value)
                  }
                />
                <RemoveButton onClick={() => handleRemoveVariant(index)}>
                  <DeleteIcon fontSize="small" />
                </RemoveButton>
              </VariantRow>
            ))}

            <AddVariantButton onClick={handleAddVariant}>
              + Add Variant
            </AddVariantButton>
            <LowStockControlsWeb value={lowStock} onChange={setLowStock} />

            <Box mt={3} mb={4} display="flex" justifyContent="flex-end">
              {saving ? (
                <Button
                  variant="contained"
                  disabled
                  style={{
                    backgroundColor: "transparent",
                    height: "36px",
                    minWidth: "100px",
                  }}
                >
                  <DotLoader />
                </Button>
              ) : (
                <>
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    style={{
                      borderColor: "#2563eb",
                      color: "#2563eb",
                      height: "36px",
                      minWidth: "100px",
                      marginRight: "1rem",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isSaveDisabled}
                    style={{
                      backgroundColor: isSaveDisabled ? "#e2e8f0" : "#2563eb",
                      color: isSaveDisabled ? "#64748b" : "#ffffff",
                      border: isSaveDisabled
                        ? "1px solid #cbd5e1"
                        : "1px solid #2563eb",
                      height: "36px",
                      minWidth: "100px",
                    }}
                  >
                    Save
                  </Button>
                </>
              )}
            </Box>
          </RightPanel>
        </ModalWrapper>
      </MotionBox>
    </Backdrop>
  );
};

export default InventoryEditModal;
